import { Suspense, useEffect, useState, type ReactNode } from "react";
import {
  HeadContent,
  Outlet,
  Scripts,
  createRootRouteWithContext,
  useRouter,
} from "@tanstack/react-router";
import type { ErrorComponentProps } from "@tanstack/react-router";
import { QueryClientProvider, type QueryClient } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "next-themes";

import appCss from "../styles.css?url";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { LoadingIndicator } from "@/components/shared/LoadingIndicator";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import { PrivacyProvider } from "@/hooks/usePrivacyMode";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ErrorReportDialog } from "@/components/ErrorReportDialog";
import { errorReportContext, type ErrorReportContext } from "@/lib/errorReport";
import { captureException } from "@/lib/sentry";
import { useErrorReporting } from "@/hooks/useErrorReporting";
import AnalyticsListener from "@/components/AnalyticsListener";
import { CookieConsent } from "@/components/CookieConsent";
import PageTransition from "@/components/PageTransition";
import { SeoTitleFallback } from "@/components/SeoTitleFallback";
import NotFound from "@/pages/NotFound";
import { reportLovableError } from "@/lib/lovable-error-reporting";
import { runClientBootstrap } from "@/lib/clientBootstrap";
import { contentSecurityPolicy, FRAME_GUARD_SCRIPT } from "@/lib/siteHead";
import { handleInPageAnchorClick } from "@/lib/inPageAnchors";
import { SkipToMainContent } from "@/components/SkipToMainContent";
import { supabase } from "@/integrations/supabase/client";
import { createSessionCacheBoundary } from "@/lib/sessionCacheBoundary";

const FONTS_HREF =
  "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  // Title, description, canonical and social tags are written per page by
  // <SEO> (React 19 hoists them into <head>, including on the server), and by
  // <SeoTitleFallback> for pages without one. Only site-wide tags live here.
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1.0" },
      { httpEquiv: "Content-Security-Policy", content: contentSecurityPolicy() },
      { httpEquiv: "X-UA-Compatible", content: "IE=edge" },
      { name: "referrer", content: "strict-origin-when-cross-origin" },
      { property: "og:image:type", content: "image/jpeg" },
      { property: "og:image:width", content: "2400" },
      { property: "og:image:height", content: "1260" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "stylesheet", href: FONTS_HREF },
      { rel: "icon", type: "image/png", sizes: "512x512", href: "/favicon.png" },
      { rel: "apple-touch-icon", sizes: "512x512", href: "/apple-touch-icon.png" },
    ],
    scripts: [{ children: FRAME_GUARD_SCRIPT }],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFound,
  errorComponent: RootErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function GlobalErrorReporter() {
  useErrorReporting();
  return null;
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  useEffect(() => {
    runClientBootstrap();
  }, []);

  // Cached data never outlives the account that loaded it (all portals share one cache).
  useEffect(() => {
    const onChange = createSessionCacheBoundary(queryClient);
    const { data } = supabase.auth.onAuthStateChange(onChange);
    return () => data.subscription.unsubscribe();
  }, [queryClient]);

  useEffect(() => {
    document.addEventListener("click", handleInPageAnchorClick);
    return () => document.removeEventListener("click", handleInPageAnchorClick);
  }, []);

  return (
    <ErrorBoundary>
      <GlobalErrorReporter />
      <QueryClientProvider client={queryClient}>
        <HelmetProvider>
          <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
            <AuthProvider>
              {/* Privacy mode wraps the whole authenticated app: the masks apply
                  wherever money or an identity is rendered, not only on dashboards. */}
              <PrivacyProvider>
                <TooltipProvider>
                  <Sonner />
                  <CookieConsent />
                  <SkipToMainContent />
                  <AnalyticsListener />
                  <SeoTitleFallback />
                  <Suspense
                    fallback={
                      <LoadingIndicator label="Loading page" size="lg" className="min-h-dvh bg-background" />
                    }
                  >
                    <PageTransition>
                      <Outlet />
                    </PageTransition>
                  </Suspense>
                </TooltipProvider>
              </PrivacyProvider>
            </AuthProvider>
          </ThemeProvider>
        </HelmetProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

function RootErrorComponent({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  // Built in an effect rather than during render: the router can render this
  // component while server-rendering a 500, and a reference generated on the
  // server would differ from the one generated during hydration.
  const [context, setContext] = useState<ErrorReportContext | null>(null);
  const [reportOpen, setReportOpen] = useState(false);

  useEffect(() => {
    const next = errorReportContext("route", error);
    setContext(next);
    console.error(error);
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
    void captureException(error, {
      tags: {
        reference: next.reference,
        route: next.route,
        surface: next.surface,
      },
    });
  }, [error]);

  return (
    <div className="min-h-dvh flex items-center justify-center bg-background text-foreground p-6">
      <div className="max-w-md w-full text-center space-y-4">
        <h1 className="text-xl font-semibold">This page didn't load</h1>
        <p className="text-muted-foreground">
          Something went wrong on our end. You can try again or head back home.
        </p>
        <div className="flex gap-2 justify-center flex-wrap">
          <button
            type="button"
            className="h-10 px-4 rounded-md bg-primary text-primary-foreground"
            onClick={() => {
              void router.invalidate();
              reset();
            }}
          >
            Try again
          </button>
          <a href="/" className="h-10 px-4 rounded-md border border-border inline-flex items-center">
            Go home
          </a>
          <button
            type="button"
            className="h-10 px-4 rounded-md border border-border inline-flex items-center disabled:opacity-50"
            disabled={!context}
            onClick={() => setReportOpen(true)}
          >
            Report an issue
          </button>
        </div>
        {context && (
          <p className="text-xs text-muted-foreground">
            Reference <span className="font-mono select-all">{context.reference}</span>
          </p>
        )}
        {context && (
          <ErrorReportDialog
            open={reportOpen}
            onOpenChange={setReportOpen}
            context={context}
          />
        )}
      </div>
    </div>
  );
}
